import { z } from "zod";

// ═══════════════════════════════════════════════════════════
// Zod Validation Schemas
// ═══════════════════════════════════════════════════════════

// ─── Enums ────────────────────────────────────────────────
export const CommissionStructureSchema = z.enum([
  "STRAIGHT",
  "SPLIT",
  "SPIFF",
  "BASE_PLUS",
  "TIERED",
]);

export const CommissionTypeSchema = z.enum([
  "PERCENTAGE",
  "FLAT",
  "FORMULA",
  "MANUAL",
  "OVERRIDE",
  "SPLIT",
]);

export const CalculationBasisSchema = z.enum([
  "GROSS_REVENUE",
  "PROFIT_MARGIN",
  "NET_REVENUE",
  "TOTAL_BEFORE_TAX",
  "CUSTOM_FIELD",
]);

export const JournalStatusSchema = z.enum([
  "PENDING",
  "CALCULATED",
  "APPROVED",
  "PAID",
  "DISPUTED",
  "CLAWBACK",
  "VOID",
]);

export const FiscalPeriodTypeSchema = z.enum(["MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"]);

// ─── Webhook Payload ─────────────────────────────────────
export const webhookPayloadSchema = z.object({
  event: z.string(),
  data: z.record(z.string(), z.unknown()),
  ts: z.string().optional(),
  auth: z.object({
    domain: z.string(),
    client_endpoint: z.string(),
    server_endpoint: z.string(),
    member_id: z.string(),
    application_token: z.string(),
  }),
});

// ─── Condition Config ────────────────────────────────────
/** Canonical operator names used by both frontend and backend */
export const conditionOperatorSchema = z.enum([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "in",
  "not_in",
  "contains",
  "is_empty",
  "is_not_empty",
]);

export const conditionConfigSchema = z.object({
  field: z.string().min(1),
  operator: conditionOperatorSchema,
  value: z.union([z.string(), z.number(), z.array(z.union([z.string(), z.number()]))]),
  logicalGroup: z.enum(["AND", "OR"]).default("AND"),
});

// ─── Formula Expression ──────────────────────────────────
// Note: ^><=! added to support exponents ({x}^2), comparisons (IF({x}>0, ...)), and inequalities.
const FORMULA_REGEX = /^[a-zA-Z0-9_.+\-*/(),%{}\s^><=!]+$/;
const MAX_EXPR_LEN = 500;

export const formulaExpressionSchema = z
  .string()
  .min(1, "Formula expression is required")
  .max(MAX_EXPR_LEN, `Formula may not exceed ${MAX_EXPR_LEN} characters`)
  .regex(FORMULA_REGEX, "Formula contains forbidden characters")
  .refine((v) => !v.includes("eval") && !v.includes("Function"), {
    message: "eval/Function calls are forbidden",
  });

// ─── Tier Config ─────────────────────────────────────────
export const tierThresholdSchema = z.object({
  level: z.number().int().min(1),
  minAmount: z.number().min(0),
  maxAmount: z.number().nonnegative().nullable().optional(),
  rate: z.number().min(0).max(100),
  flatBonus: z.number().min(0).optional(),
});

export const tierConfigSchema = z.object({
  mode: z.enum(["INCREMENTAL", "RETROACTIVE"]),
  thresholds: z.array(tierThresholdSchema).min(1).max(20),
  basis: z
    .enum([
      // Current values
      "CUMULATIVE_REVENUE",
      "DEAL_COUNT",
      "EARNED_COMMISSIONS",
      "CUSTOM_FIELD",
      // Legacy aliases — migrated via transform below
      "QUOTA_ATTAINMENT_PCT",
      "UNITS_SOLD",
    ])
    .default("CUMULATIVE_REVENUE")
    .transform((v) => {
      if (v === "UNITS_SOLD") return "DEAL_COUNT" as const;
      if (v === "QUOTA_ATTAINMENT_PCT") return "CUSTOM_FIELD" as const;
      return v;
    }),
  period: z.enum(["MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"]).default("MONTHLY"),
  scope: z.enum(["THIS_RULE", "ALL_RULES"]).default("THIS_RULE"),
  dedupByEntity: z.boolean().default(true),
});

// ─── Cap Config ──────────────────────────────────────────
export const capConfigSchema = z
  .object({
    cap: z.number().min(0).optional(),
    floor: z.number().min(0).optional(),
  })
  .refine((v) => v.cap == null || v.floor == null || v.cap >= v.floor, {
    message: "Cap must be greater than or equal to floor",
  });

// ─── Base-Plus Config ────────────────────────────────────
export const basePlusConfigSchema = z.object({
  baseAmount: z.number().min(0),
  // basePeriod removed — engine never reads it; period tracking is manual
});

// ─── Product Rate Config ──────────────────────────────────
export const productRateEntrySchema = z.object({
  productId: z.number().int().positive(),
  productName: z.string().min(1),
  rateType: z.enum(["PERCENTAGE", "FLAT"]),
  rate: z.number().nonnegative(),
  basePrice: z.number().nonnegative().optional(),
  baseCurrency: z.string().optional(),
});

export const productRateConfigSchema = z
  .object({
    mode: z.enum(["TABLE", "CATALOG_FIELD"]),
    entries: z.array(productRateEntrySchema).default([]),
    catalogRateFieldId: z.string().optional(),
    catalogRateFieldLabel: z.string().optional(),
    catalogRateType: z.enum(["PERCENTAGE", "FLAT"]).optional(),
  })
  .superRefine((cfg, ctx) => {
    if (cfg.mode === "TABLE" && cfg.entries.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "TABLE mode requires at least one product rate entry",
        path: ["entries"],
      });
    }
    if (cfg.mode === "CATALOG_FIELD" && !cfg.catalogRateFieldId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "CATALOG_FIELD mode requires a catalog field ID",
        path: ["catalogRateFieldId"],
      });
    }
  });

// ─── Trigger Config ──────────────────────────────────────
export const triggerConfigSchema = z
  .object({
    /** New format: numeric Bitrix24 entity type ID (e.g. 2 = Deal, 1 = Lead) */
    entityTypeId: z.number().int().positive().optional(),
    /** @deprecated Legacy string entity type — kept for backward compat with stored rules */
    entityType: z.enum(["DEAL", "INVOICE", "SMART_PROCESS"]).optional(),
    events: z.array(z.enum(["ADD", "UPDATE", "DELETE"])).min(1),
    /** UI mode for trigger configuration — SIMPLE = guided wizard, ADVANCED = raw FilterBuilder */
    triggerMode: z.enum(["SIMPLE", "ADVANCED"]).optional(),
    stageSemantic: z.enum(["S", "F", "CUSTOM"]).optional(),
    stageIds: z.array(z.string()).optional(),
    categoryIds: z.array(z.number()).optional(),
  })
  .refine((v) => v.entityTypeId != null || v.entityType != null, {
    message: "Either entityTypeId or entityType is required in trigger config",
  });

// ─── Rule Condition (matches RuleConditionConfig domain type) ──
const ruleConditionSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    field: z.string().optional(),
    op: z.string().optional(),
    value: z
      .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
      .optional(),
    group: z
      .object({
        operator: z.enum(["AND", "OR"]),
        conditions: z.array(ruleConditionSchema),
      })
      .optional(),
  }),
);

export const ruleConditionConfigSchema = z.object({
  operator: z.enum(["AND", "OR"]),
  conditions: z.array(ruleConditionSchema).default([]),
});

// ─── QueryFilterConfig (Phase 5+ new condition format) ───
/** Permissive value schema — shape is validated at evaluation time */
const filterRuleValueSchema: z.ZodType<unknown> = z.any();

const filterRuleSchema = z.object({
  id: z.string(),
  field: z.string().min(1),
  operator: z.string().min(1),
  value: filterRuleValueSchema,
});

const filterGroupSchema = z.object({
  id: z.string(),
  logic: z.enum(["AND", "OR"]),
  enabled: z.boolean(),
  rules: z.array(filterRuleSchema),
});

export const queryFilterConfigSchema = z.object({
  topLogic: z.enum(["AND", "OR"]),
  groups: z.array(filterGroupSchema),
});

/**
 * Accepts either:
 * - New QueryFilterConfig `{ topLogic, groups }` (Phase 5+)
 * - Legacy RuleConditionConfig `{ operator, conditions }` (pre-Phase 5, migrated on read)
 */
export const conditionsSchema = z.union([queryFilterConfigSchema, ruleConditionConfigSchema]);

// ─── Complete Rule Create Schema ─────────────────────────
export const ruleCreateSchema = z
  .object({
    /** Whether the rule should be active immediately after create/save. Defaults to true. */
    isActive: z.boolean().default(true),
    ruleName: z.string().min(1).max(255),
    structure: CommissionStructureSchema,
    commissionType: CommissionTypeSchema,
    calculationBasis: CalculationBasisSchema,
    currency: z.string().length(3).default("USD"),
    triggers: triggerConfigSchema,
    conditions: conditionsSchema.optional(),
    formula: z
      .object({
        mode: z.enum(["SINGLE", "ITERATION"]).default("SINGLE"),
        expression: formulaExpressionSchema,
        /** Record<varName, FormulaVariable> — well-known vars auto-injected by backend */
        variables: z
          .record(
            z.string(),
            z.object({
              source: z.enum([
                "DEAL",
                "PRODUCT_ROW",
                "CONTACT",
                "COMPANY",
                "COMPUTED",
                "ENTITY",
                "CUSTOM",
              ]),
              field: z.string(),
              defaultValue: z.number().optional(),
              type: z.enum(["number", "currency"]),
              expression: z.string().optional(),
            }),
          )
          .default({}),
        modifiers: z
          .array(
            z.object({
              type: z.enum(["CAP", "FLOOR", "ROUND"]),
              value: z.number(),
            }),
          )
          .default([]),
      })
      .nullish(),
    tiers: tierConfigSchema.nullish(),
    caps: capConfigSchema.nullish(),
    // Base-plus salary + variable commission
    basePlusConfig: basePlusConfigSchema.nullish(),
    // Split commission allocation (structure = SPLIT)
    splitConfig: z
      .object({
        allocations: z
          .array(
            z.object({
              rep: z.object({
                sourceType: z.enum(["CRM_FIELD", "FIXED_USER"]),
                fieldRef: z
                  .object({
                    fieldId: z.string().min(1),
                    fieldLabel: z.string(),
                    isMultiple: z.boolean(),
                  })
                  .optional(),
                fixedUser: z
                  .object({
                    userId: z.number().int().positive(),
                    userLabel: z.string(),
                  })
                  .optional(),
              }),
              percentage: z.number().min(0).max(100),
              roleLabel: z.string().optional(),
              // Legacy fields — accepted during migration, ignored on write
              userId: z.number().optional(),
              userLabel: z.string().optional(),
              role: z.enum(["AE", "SDR", "SE", "MANAGER", "CUSTOM"]).optional(),
            }),
          )
          .min(2, "At least 2 allocations are required for split commissions"),
      })
      .superRefine((cfg, ctx) => {
        const total = cfg.allocations.reduce((s, a) => s + a.percentage, 0);
        if (Math.abs(total - 100) > 0.01) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Split allocations must total 100% (currently ${total.toFixed(1)}%)`,
            path: ["allocations"],
          });
        }
        cfg.allocations.forEach((a, i) => {
          if (a.percentage <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Each allocation must have a percentage greater than 0%",
              path: ["allocations", i, "percentage"],
            });
          }
        });
      })
      .nullish(),
    // Clawback configuration
    clawbackConfig: z
      .object({
        windowDays: z.number().int().min(1).max(730),
        clawbackOnDelete: z.boolean(),
        triggerMode: z.enum(["SIMPLE", "ADVANCED"]),
        triggerSemantic: z.enum(["F", "S"]).optional(),
        triggerStageIds: z.array(z.string()).optional(),
        triggerCategoryIds: z.array(z.number()).optional(),
        triggerConditions: z
          .object({
            topLogic: z.enum(["AND", "OR"]),
            groups: z.array(z.any()),
          })
          .optional(),
      })
      .refine(
        (cfg) => {
          if (cfg.triggerMode === "SIMPLE") {
            return !!cfg.triggerSemantic || (cfg.triggerStageIds && cfg.triggerStageIds.length > 0);
          }
          if (cfg.triggerMode === "ADVANCED") {
            return cfg.triggerConditions && cfg.triggerConditions.groups.length > 0;
          }
          return true;
        },
        {
          message:
            "SIMPLE mode requires a semantic or stage IDs; ADVANCED mode requires at least one condition group",
        },
      )
      .nullish(),
    // SPIFF bonus configuration (structure = SPIFF)
    spiffConfig: z
      .object({
        spiffMode: z.enum(["BONUS_ONLY", "STACKING"]).optional(),
        bonusAmount: z.number().positive(),
        bonusType: z.enum(["FLAT", "PERCENTAGE"]),
        validFrom: z.string().refine((v) => !isNaN(Date.parse(v)), "Invalid date"),
        validTo: z.string().refine((v) => !isNaN(Date.parse(v)), "Invalid date"),
      })
      .superRefine((cfg, ctx) => {
        if (!isNaN(Date.parse(cfg.validFrom)) && !isNaN(Date.parse(cfg.validTo))) {
          if (new Date(cfg.validTo) <= new Date(cfg.validFrom)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "SPIFF end date must be after the start date",
              path: ["validTo"],
            });
          }
        }
      })
      .nullish(),
    // Per-product commission rate overrides (ITERATION mode only)
    productRateConfig: productRateConfigSchema.nullish(),
    // Post-calculation action flags (map of plugin configs)
    actionsConfig: z
      .object({
        AUTO_APPROVE: z.object({ enabled: z.boolean() }).optional(),
        NOTIFY_ASSIGNEE: z
          .object({
            enabled: z.boolean(),
            messageTemplate: z.string().max(500).optional(),
          })
          .optional(),
        LOCK_AFTER_DAYS: z
          .object({
            enabled: z.boolean(),
            days: z.number().int().min(1).max(365),
          })
          .optional(),
        CRM_TIMELINE_LOG: z
          .object({
            enabled: z.boolean(),
            messageTemplate: z.string().max(500).optional(),
          })
          .optional(),
      })
      .nullish(),
    effectiveFrom: z
      .string()
      .refine((v) => !isNaN(Date.parse(v)), "Invalid date")
      .optional(),
    /** null = clear an existing end date; undefined = not sent (no change in PUT) */
    effectiveTo: z
      .string()
      .refine((v) => !isNaN(Date.parse(v)), "Invalid date")
      .nullish(),
    priority: z.number().int().min(1).max(999).default(100),
    notes: z.string().max(2000).optional(),
    // Dynamic field references (take precedence over legacy enums when present)
    basisFieldRef: z
      .object({
        fieldId: z.string().min(1),
        fieldLabel: z.string(),
        fieldType: z.string(),
        source: z.enum(["ENTITY", "PRODUCT_ROW"]),
      })
      .nullish(),
    tierAccumulatorRef: z
      .object({
        fieldId: z.string().min(1),
        fieldLabel: z.string(),
        fieldType: z.string(),
        source: z.enum(["ENTITY", "PRODUCT_ROW"]),
      })
      .nullish(),
    // Default rep resolver — who receives the commission (all non-SPLIT structures)
    defaultRepResolver: z
      .object({
        sourceType: z.enum(["CRM_FIELD", "FIXED_USER"]),
        fieldRef: z
          .object({
            fieldId: z.string().min(1),
            fieldLabel: z.string(),
            isMultiple: z.boolean(),
          })
          .optional(),
        fixedUser: z
          .object({
            userId: z.number().int().positive(),
            userLabel: z.string(),
          })
          .optional(),
      })
      .nullish(),
  })
  .superRefine((data, ctx) => {
    // Cross-field: SPIFF structure requires spiffConfig
    if (data.structure === "SPIFF" && !data.spiffConfig) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "spiffConfig is required when structure is SPIFF",
        path: ["spiffConfig"],
      });
    }
    // Cross-field: TIERED structure with CUSTOM_FIELD basis requires tierAccumulatorRef
    if (
      data.structure === "TIERED" &&
      data.tiers?.basis === "CUSTOM_FIELD" &&
      !data.tierAccumulatorRef
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "tierAccumulatorRef is required when tiers basis is CUSTOM_FIELD",
        path: ["tierAccumulatorRef"],
      });
    }
  });

export type RuleCreateInput = z.infer<typeof ruleCreateSchema>;

// ─── Data Deletion (TPP §7.4) ────────────────────────────

/** Simple deletion target enum */
export const DeletionTargetSchema = z.enum(["JOURNAL", "RULES", "BOTH"]);
export type DeletionTarget = z.infer<typeof DeletionTargetSchema>;

/** Discriminated union of all 6 deletion modes */
export const DataDeletionRequestSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("BY_PERIOD"),
    target: DeletionTargetSchema.default("JOURNAL"),
    periodFrom: z.string().regex(/^\d{4}-\d{2}$/),
    periodTo: z.string().regex(/^\d{4}-\d{2}$/),
  }),
  z.object({
    mode: z.literal("BY_REP"),
    repIds: z.array(z.number().int().positive()).min(1).max(50),
  }),
  z.object({
    mode: z.literal("BY_RULE"),
    ruleIds: z.array(z.number().int().positive()).min(1).max(50),
  }),
  z.object({
    mode: z.literal("BY_ENTRY"),
    target: z.enum(["JOURNAL", "RULES"]),
    entryIds: z.array(z.number().int().positive()).min(1).max(200),
  }),
  z.object({
    mode: z.literal("ALL"),
    target: DeletionTargetSchema,
    confirmationText: z.string(),
  }),
  z.object({
    mode: z.literal("DESTROY_STORAGE"),
    confirmMemberId: z.string(),
  }),
]);

export type DataDeletionRequest = z.infer<typeof DataDeletionRequestSchema>;
export type DataDeletionMode = DataDeletionRequest["mode"];

/** Legacy simple enum — kept for backward compatibility */
export const DataDeletionModeSchema = z.enum([
  "BY_PERIOD",
  "BY_REP",
  "BY_RULE",
  "BY_ENTRY",
  "ALL",
  "DESTROY_STORAGE",
]);

// ─── Commission Status Transition (D15 Security Hardening) ──
export const statusTransitionSchema = z.object({
  status: JournalStatusSchema,
  notes: z.string().max(2000).optional(),
  overrideAmount: z.number().finite().optional(),
});

export type StatusTransitionInput = z.infer<typeof statusTransitionSchema>;

// Accepts YYYY-MM, YYYY-QN, YYYY-HN, or YYYY (all fiscal period formats)
const periodSchema = z
  .string()
  .regex(
    /^\d{4}(-\d{2}|-Q[1-4]|-H[12])?$/,
    "period must be YYYY-MM, YYYY-QN, YYYY-HN, or YYYY format",
  );

// ─── Recalculate Request (D15 Security Hardening) ────────
const recalculateScopeSchema = z.union([
  z.literal("ALL"),
  z.object({ ruleIds: z.array(z.number().int().positive()).min(1) }),
  z.object({ repIds: z.array(z.number().int().positive()).min(1) }),
]);

export const recalculateRequestSchema = z.object({
  period: periodSchema,
  scope: recalculateScopeSchema.default("ALL"),
  dryRun: z.boolean().optional().default(false),
  page: z.number().int().nonnegative().optional().default(0),
  pageSize: z.number().int().min(1).max(100).optional().default(50),
});

export type RecalculateRequestInput = z.infer<typeof recalculateRequestSchema>;

// ─── List / Filter Requests ──────────────────────────────
export const listRequestSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
  sortField: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  filters: z.record(z.string(), z.unknown()).optional(),
});

export type ListRequestInput = z.infer<typeof listRequestSchema>;

// ─── Historical Sync (backfill) ──────────────────────────
// Dates arrive as bare "YYYY-MM-DD" and are resolved server-side by
// parseDateBoundaryUnix, which is what makes dateTo mean "through the end of
// that day" rather than midnight at its start (see DEV_LEDGER CORE-01).
const bareDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected a calendar date in YYYY-MM-DD form");

export const historicalSyncRequestSchema = z
  .object({
    entityTypeIds: z.array(z.number().int().positive()).min(1).max(20),
    dateField: z.enum(["createdTime", "updatedTime", "closeDate"]),
    dateFrom: bareDateSchema,
    dateTo: bareDateSchema,
  })
  .refine((v) => v.dateFrom <= v.dateTo, {
    message: "dateFrom must be on or before dateTo",
    path: ["dateFrom"],
  });

export type HistoricalSyncRequestInput = z.infer<typeof historicalSyncRequestSchema>;
